import { ref, computed } from 'vue';
import { stripChartPayload } from '@/composables/useChartStatus';
import { buildChartSignature } from '@/utils/chartSignature';
import {
  ensureFocalOptions,
  resolveSelectedFocalOption,
} from '@/utils/discoveryFocalOptions';
import { normalizeBirthTimeAccuracy } from '@/utils/discoveryBirthTime';
import { resolveHouseSystem } from '@/utils/discoveryHouseSystem';

export const DISCOVERY_STORAGE_KEY = 'skyspeaks.personalSkyDiscovery';
export const DISCOVERY_VERSION = 2;

const discovery = ref(null);

function nowIso() {
  return new Date().toISOString();
}

function createEmptyBirthForm() {
  return {
    previewName: '',
    month: 1,
    day: 1,
    year: 1990,
    hour: 12,
    minute: 0,
    city: '',
    region: '',
    country: '',
    country_code: '',
    long: null,
    lat: null,
  };
}

export function createEmptyDiscovery() {
  const timestamp = nowIso();
  return {
    version: DISCOVERY_VERSION,
    previewName: '',
    birthTimeAccuracy: 'known',
    birthForm: createEmptyBirthForm(),
    birthData: null,
    chartData: null,
    chartMeta: {
      mode: 'natal',
      birthTimeAccuracy: 'known',
    },
    chartSignature: '',
    focalOptions: [],
    selectedFocalOptionId: null,
    preview: null,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

function birthFormFromBirthData(birthData) {
  if (!birthData) {
    return createEmptyBirthForm();
  }
  const num = (v, def) => (v != null && v !== '' && !isNaN(Number(v)) ? Number(v) : def);
  return {
    previewName: '',
    month: num(birthData.month, 1),
    day: num(birthData.day, 1),
    year: num(birthData.year, 1990),
    hour: num(birthData.hour, 12),
    minute: num(birthData.minute, 0),
    city: birthData.city ?? '',
    region: birthData.region ?? '',
    country: birthData.country ?? '',
    country_code: birthData.country_code ?? '',
    long: birthData.long ?? null,
    lat: birthData.lat ?? null,
  };
}

function migrateV1ToV2(v1) {
  if (!v1 || typeof v1 !== 'object') {
    return null;
  }

  const previewName = typeof v1.context?.previewName === 'string'
    ? v1.context.previewName
    : '';

  const birthForm = birthFormFromBirthData(v1.birthData);
  birthForm.previewName = previewName;

  return {
    version: DISCOVERY_VERSION,
    previewName,
    birthTimeAccuracy: 'known',
    birthForm,
    birthData: v1.birthData ?? null,
    chartData: v1.chartData ?? null,
    chartMeta: {
      mode: 'natal',
      birthTimeAccuracy: 'known',
    },
    chartSignature: buildChartSignature(v1.chartData, { birthTimeAccuracy: 'known' }),
    focalOptions: [],
    selectedFocalOptionId: null,
    preview: v1.insight ?? null,
    createdAt: v1.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  };
}

function normalizeV2(state) {
  const birthTimeAccuracy = normalizeBirthTimeAccuracy(state.birthTimeAccuracy);
  const birthForm = {
    ...createEmptyBirthForm(),
    ...(state.birthForm ?? {}),
    previewName: state.previewName ?? state.birthForm?.previewName ?? '',
  };

  return {
    ...createEmptyDiscovery(),
    ...state,
    version: DISCOVERY_VERSION,
    previewName: birthForm.previewName?.trim() ?? '',
    birthTimeAccuracy,
    birthForm,
    chartMeta: {
      mode: state.chartMeta?.mode ?? 'natal',
      birthTimeAccuracy,
    },
    selectedFocalOptionId: state.selectedFocalOptionId ?? null,
    focalOptions: Array.isArray(state.focalOptions) ? state.focalOptions : [],
    chartSignature: state.chartSignature
      ?? buildChartSignature(state.chartData, { birthTimeAccuracy }),
  };
}

export function loadStoredDiscovery() {
  return readDiscoveryFromStorage();
}

export function clearStoredDiscovery() {
  writeDiscoveryToStorage(null);
}

/**
 * Read discovery from sessionStorage, falling back to localStorage.
 *
 * Dual storage is intentional: email verification links usually open in a new
 * tab, and sessionStorage is not shared across tabs. localStorage keeps the
 * pending preview chart available through signup → verify → Continue.
 */
function readDiscoveryFromStorage() {
  try {
    let raw = sessionStorage.getItem(DISCOVERY_STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(DISCOVERY_STORAGE_KEY);
      if (raw) {
        // Hydrate this tab's session so in-flow reads stay consistent.
        sessionStorage.setItem(DISCOVERY_STORAGE_KEY, raw);
      }
    }
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (parsed.version === 1) {
      return migrateV1ToV2(parsed);
    }
    if (parsed.version === DISCOVERY_VERSION) {
      return normalizeV2(parsed);
    }
    return null;
  } catch {
    return null;
  }
}

function writeDiscoveryToStorage(state) {
  if (state == null) {
    sessionStorage.removeItem(DISCOVERY_STORAGE_KEY);
    localStorage.removeItem(DISCOVERY_STORAGE_KEY);
    discovery.value = null;
    return;
  }
  const serialized = JSON.stringify(state);
  sessionStorage.setItem(DISCOVERY_STORAGE_KEY, serialized);
  localStorage.setItem(DISCOVERY_STORAGE_KEY, serialized);
  discovery.value = state;
}

function touchDiscovery(state) {
  return {
    ...state,
    updatedAt: nowIso(),
  };
}

export function buildDiscoveryCalcChartPayload({
  birthForm,
  previewName,
  birthTimeAccuracy,
  houseSystemChoice,
}) {
  const accuracy = normalizeBirthTimeAccuracy(birthTimeAccuracy);
  const hour = accuracy === 'unknown' ? 12 : birthForm.hour;
  const minute = accuracy === 'unknown' ? 0 : birthForm.minute;
  const name = (previewName ?? birthForm.previewName ?? '').trim();

  return {
    firstName: name,
    lastName: '',
    month: birthForm.month,
    day: birthForm.day,
    year: birthForm.year,
    hour,
    minute,
    city: birthForm.city ?? '',
    region: birthForm.region ?? '',
    country: birthForm.country ?? '',
    country_code: birthForm.country_code ?? '',
    long: birthForm.long,
    lat: birthForm.lat,
    // Carried into the chart-calculation request itself so an unknown-time
    // noon substitution (see `hour`/`minute` above) is never indistinguishable
    // downstream from a genuinely known noon birth.
    birth_time_accuracy: accuracy,
    // EP house-system product policy (see discoveryHouseSystem.js): known/
    // approximate always resolve to "equal"; unknown resolves to "solar"
    // (default) or null per the visitor's explicit choice. astro itself
    // makes no assumption here -- this is the caller deciding.
    house_system: resolveHouseSystem(accuracy, houseSystemChoice),
  };
}

export function usePersonalSkyDiscovery() {
  const hasDiscoveryResult = computed(
    () => Boolean(discovery.value?.chartData && discovery.value?.preview),
  );

  function loadDiscovery() {
    const stored = readDiscoveryFromStorage();
    if (stored) {
      const normalized = normalizeV2(stored);
      discovery.value = normalized;
      writeDiscoveryToStorage(touchDiscovery(normalized));
      return normalized;
    }
    const empty = createEmptyDiscovery();
    discovery.value = empty;
    return empty;
  }

  function saveDiscovery(state) {
    writeDiscoveryToStorage(touchDiscovery(normalizeV2(state)));
  }

  function clearDiscovery() {
    writeDiscoveryToStorage(null);
  }

  function setBirthSubmission({
    birthForm,
    previewName,
    birthTimeAccuracy,
    rawResponse,
  }) {
    const current = discovery.value ?? createEmptyDiscovery();
    const accuracy = normalizeBirthTimeAccuracy(birthTimeAccuracy);
    const trimmedName = (previewName ?? birthForm?.previewName ?? '').trim();
    const normalizedForm = {
      ...createEmptyBirthForm(),
      ...birthForm,
      previewName: trimmedName,
      hour: accuracy === 'unknown' ? 12 : birthForm.hour,
      minute: accuracy === 'unknown' ? 0 : birthForm.minute,
    };

    const {
      birth_data: responseBirthData,
      persisted: _persisted,
      persistence_error: _persistenceError,
      ...rawPayload
    } = rawResponse;

    const chartData = stripChartPayload(rawPayload);
    const next = touchDiscovery({
      ...current,
      previewName: trimmedName,
      birthTimeAccuracy: accuracy,
      birthForm: normalizedForm,
      birthData: responseBirthData ?? buildDiscoveryCalcChartPayload({
        birthForm: normalizedForm,
        previewName: trimmedName,
        birthTimeAccuracy: accuracy,
      }),
      chartData,
      chartMeta: {
        mode: 'natal',
        birthTimeAccuracy: accuracy,
      },
      chartSignature: buildChartSignature(chartData, { birthTimeAccuracy: accuracy }),
      focalOptions: [],
      selectedFocalOptionId: null,
      preview: null,
    });
    saveDiscovery(next);
    return next;
  }

  function setFocalOptions(focalOptions) {
    const current = discovery.value ?? createEmptyDiscovery();
    const next = touchDiscovery({
      ...current,
      focalOptions: Array.isArray(focalOptions) ? focalOptions : [],
    });
    saveDiscovery(next);
    return next;
  }

  function setSelectedFocalOption(selectedFocalOptionId) {
    const current = discovery.value ?? createEmptyDiscovery();
    const next = touchDiscovery({
      ...current,
      selectedFocalOptionId: selectedFocalOptionId || null,
    });
    saveDiscovery(next);
    return next;
  }

  function setPreview(preview) {
    const current = discovery.value ?? createEmptyDiscovery();
    const next = touchDiscovery({
      ...current,
      preview,
    });
    saveDiscovery(next);
    return next;
  }

  return {
    discovery,
    hasDiscoveryResult,
    loadDiscovery,
    saveDiscovery,
    clearDiscovery,
    setBirthSubmission,
    setFocalOptions,
    setSelectedFocalOption,
    setPreview,
    ensureFocalOptions,
    resolveSelectedFocalOption,
    createEmptyBirthForm,
  };
}
